import cn, { Argument as ClassName } from 'classnames'
import jstz from 'jstz'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

import useDefer, { Status } from '../../common/hooks/useDefer'
import Input from '../../common/components/Input'
import Toggle from '../../common/components/Toggle'
import usePatch from '../../common/hooks/usePatch'
import { SFormRow } from '../../common/styles/Form'
import BootstrapModalLayout from '../../ui/BootstrapModalLayout'
import Text from '../../ui/Text'
import { useAdaptConnectorNaming } from '../adaptConnectorNaming'
import { Alert } from '../alert'
import { TooltipIcon } from '../icons/tooltip-icon'
import { chargePointScheduleType } from '../loadChargePointSchedules'
import saveSchedule from '../saveSchedule'
import { TimePicker } from '../time-picker'
import useText from '../../common/hooks/useText'
import { ChargePoint } from '../../common/redux/api/web-api'

const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`

const InfoMessage = styled.div`
    background-color: ${({ theme }) => theme.brandColors.CA_Light_Blue};
    border-radius: 8px;
    padding: 21px 20px;
`

const ToggleContainer = styled.span`
    display: flex;
    align-items: center;
    gap: 4px;
    color: ${({ theme }) => theme.font.midDarkColor};
    font-weight: 500;
`

const RowContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 2px;
`
const DaySlot = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`

const newSchedule = {
    active: true,
    chargePointId: '',
    connectorIdList: '1,2',
    connectorIds: [1, 2],
    connectors: [],
    endHours: 0,
    endMinutes: 0,
    name: '',
    startHours: 0,
    startMinutes: 0,
    timeZone: jstz.determine().name(),
}

export type ChargePointCapiScheduleChargingModalProps = {
    className?: ClassName
    closeModal: (update: boolean) => void
    schedule?: chargePointScheduleType
    chargePoint: ChargePoint
}

export const ChargePointCapiScheduleChargingModal = React.memo(function ChargePointCapiScheduleChargingModal({
    className,
    closeModal,
    schedule: editableSchedule,
    chargePoint,
}: ChargePointCapiScheduleChargingModalProps): React.ReactElement {
    const text = useText()
    const [showInfo, setShowInfo] = useState(false)
    const [errorMsg, setErrorMsg] = useState('')
    const { apply: updateSchedule, value: schedule } = usePatch<chargePointScheduleType>(
        editableSchedule ?? newSchedule
    )

    const adaptConnectorNaming = useAdaptConnectorNaming()

    useEffect(() => {
        const connectors = chargePoint.connectors?.map((connector) => ({
            name: adaptConnectorNaming(connector.name),
            connectorId: connector.connectorId,
            affected: schedule.connectorIds.includes(connector.connectorId),
        }))
        updateSchedule({ connectors: connectors, chargePointId: chargePoint.id })
    }, [])

    const toggleConnector = (c: { name: string; connectorId: number; affected: boolean }) => {
        const connector = schedule.connectors.find((sc) => sc.connectorId === c.connectorId)
        if (connector) {
            connector.affected = !connector.affected
            updateSchedule({
                connectors: [...schedule.connectors.filter((sc) => sc.connectorId !== c.connectorId), connector],
            })
        }
    }

    const save = useDefer((schedule: chargePointScheduleType) => saveSchedule(schedule))

    useEffect(() => {
        if (save.status === Status.ERROR) setErrorMsg('msgScheduleError')

        if (save.status === Status.SUCCESS) closeModal(true)
    }, [save.status])

    const handleSubmit = () => {
        const includedIds = schedule.connectors
            .map((s) => {
                if (s.affected) return s.connectorId
            })
            .filter((c) => c !== undefined)
        setErrorMsg('')
        save.execute({ ...schedule, connectorIdList: includedIds.toString() }).catch(() => {})
    }
    const cancel = () => closeModal(false)
    return (
        <BootstrapModalLayout
            className={cn(className)}
            size="sm"
            title={
                <Header>
                    <span className="margin-top-4">
                        {editableSchedule ? (
                            <Text code="lblSchedule" values={{ name: schedule.name }} />
                        ) : (
                            <Text code="lblNewScheduleModal" />
                        )}
                    </span>
                    <span onClick={() => setShowInfo(!showInfo)} data-testid="info-btn">
                        <TooltipIcon size={18}></TooltipIcon>
                    </span>
                </Header>
            }
            actions={[
                {
                    btn: 'btn-secondary',
                    label: text('btnCancel'),
                    callback: cancel,
                },
                {
                    label: text('btnSave'),
                    callback: handleSubmit,
                    loading: save.status === Status.PENDING,
                    disabled: save.status === Status.PENDING || schedule.name.length === 0,
                },
            ]}
        >
            {showInfo && (
                <InfoMessage data-testid="info-text">
                    <Text
                        code="msgScheduleInfo"
                        values={{
                            test: (
                                <>
                                    <span className="fa fa-toggle-off grayFont aweFontSize"></span>
                                    <span className="fa fa-clock-o redFont aweFontSize"></span>
                                </>
                            ),
                        }}
                    />
                </InfoMessage>
            )}
            {errorMsg && <Alert message={text(errorMsg)} />}
            <SFormRow>
                <Input
                    defaultValue={schedule.name}
                    onChange={(e) => updateSchedule({ name: e.target.value })}
                    data-testid="name-field"
                    id="schedule-name-input"
                    labelText={text('lblName')}
                />
            </SFormRow>
            <SFormRow>
                <Toggle
                    isToggled={schedule.active}
                    toggleFn={() => updateSchedule({ active: !schedule.active })}
                    disabled={false}
                    data-testid="schedule-active"
                    toggleText={text(schedule.active ? 'lblActive' : 'lblInactiveStatus')}
                    className="right-align"
                />
            </SFormRow>
            <SFormRow>
                <label>
                    <Text code="lblActiveDaysField" />
                </label>
                <RowContainer>
                    <DaySlot>
                        <Text code="lblShortestMonday" />
                        <input
                            type="checkbox"
                            className="checkbox-black"
                            defaultChecked={schedule?.monday}
                            onChange={(e) => updateSchedule({ monday: e.target.checked })}
                        />
                    </DaySlot>
                    <DaySlot>
                        <Text code="lblShortestTuesday" />
                        <input
                            type="checkbox"
                            className="checkbox-black"
                            defaultChecked={schedule?.tuesday}
                            onChange={(e) => updateSchedule({ tuesday: e.target.checked })}
                        />
                    </DaySlot>
                    <DaySlot>
                        <Text code="lblShortestWednesday" />
                        <input
                            type="checkbox"
                            className="checkbox-black"
                            defaultChecked={schedule?.wednesday}
                            onChange={(e) => updateSchedule({ wednesday: e.target.checked })}
                        />
                    </DaySlot>
                    <DaySlot>
                        <Text code="lblShortestThursday" />
                        <input
                            type="checkbox"
                            className="checkbox-black"
                            defaultChecked={schedule?.thursday}
                            onChange={(e) => updateSchedule({ thursday: e.target.checked })}
                        />
                    </DaySlot>
                    <DaySlot>
                        <Text code="lblShortestFriday" />
                        <input
                            type="checkbox"
                            className="checkbox-black"
                            defaultChecked={schedule?.friday}
                            onChange={(e) => updateSchedule({ friday: e.target.checked })}
                        />
                    </DaySlot>
                    <DaySlot>
                        <Text code="lblShortestSaturday" />
                        <input
                            type="checkbox"
                            className="checkbox-black"
                            defaultChecked={schedule?.saturday}
                            onChange={(e) => updateSchedule({ saturday: e.target.checked })}
                        />
                    </DaySlot>
                    <DaySlot>
                        <Text code="lblShortestSunday" />
                        <input
                            type="checkbox"
                            className="checkbox-black"
                            defaultChecked={schedule?.sunday}
                            onChange={(e) => updateSchedule({ sunday: e.target.checked })}
                        />
                    </DaySlot>
                </RowContainer>
            </SFormRow>
            <SFormRow>
                <RowContainer style={{ gap: '28px' }}>
                    <div>
                        <Text code="lblFromField" />
                        <TimePicker
                            setTime={(h, m) => updateSchedule({ startHours: h, startMinutes: m })}
                            defaultHour={schedule.startHours}
                            defaultMinute={schedule.startMinutes}
                        />
                    </div>
                    <div>
                        <Text code="lblToField" />
                        <TimePicker
                            setTime={(h, m) => updateSchedule({ endHours: h, endMinutes: m })}
                            defaultHour={schedule.endHours}
                            defaultMinute={schedule.endMinutes}
                        />
                    </div>
                </RowContainer>
            </SFormRow>
            <SFormRow>
                <div className="margin-bottom-10">
                    <Text code="lblAffectedByScheduleSection" />
                </div>
                <RowContainer>
                    {schedule?.connectors
                        ?.sort((a, b) => a.connectorId - b.connectorId)
                        .map((c, i) => (
                            <div key={i}>
                                {adaptConnectorNaming(c.name)}
                                <ToggleContainer className="align-center">
                                    <Toggle
                                        isToggled={c.affected}
                                        toggleFn={() => toggleConnector(c)}
                                        disabled={false}
                                        data-testid={'connector-' + c.connectorId}
                                        showStateLabel={true}
                                        labelPos="right"
                                    />
                                </ToggleContainer>
                            </div>
                        ))}
                </RowContainer>
            </SFormRow>
        </BootstrapModalLayout>
    )
})
